/**
 * Seed graph for the Supplier-document extraction pipeline, laid out for the
 * AI Tooling flow canvas. Mirrors the real pipeline documented in
 * `docs/supplier-document-extraction.md` and implemented in
 * `functions-suppliers/src/ai/orchestrate-extraction.ts`:
 *
 *   trigger -> classifier -> router --content_types--> accommodation
 *                                   |               -> green fees
 *                                   |               -> packages
 *                                   --classification-> stop sales (planned)
 *                                   |               -> promotions (planned)
 *                                   `--classify-only-> (contract / information)
 *   every branch -> index (deterministic search-hook denormalisation)
 *
 * Node `data.binding`:
 *   - "tool"        -> bound to a registry AI tool; its model / prompts / schema
 *                      read & write the live `ai_tooling/{toolId}` Firestore doc.
 *   - "placeholder" -> a planned agent not yet wired to the backend. Its draft
 *                      config lives inline on the node (`data.draft`).
 *
 * Edge `data.condition` is the routing trigger the orchestrator uses:
 *   - { kind: "always" }                              -> unconditional
 *   - { kind: "classification", value: "supplier.x" } -> on a classifier verdict
 *   - { kind: "content_type", value: "accommodation" } -> on a rate-card content flag
 *
 * This is only a seed: once a flow is saved to `ai_flows/{flowId}` the stored
 * copy wins and this file is the first-open fallback.
 */

export const SUPPLIER_DOCUMENT_FLOW_ID = "supplier-document"

// Draft schema sketches for the not-yet-built specialist readers, transcribed
// from docs/supplier-document-extraction.md sections 6.2 / 6.3 so the planned
// nodes carry a meaningful "expected outputs" surface on the canvas.
const STOP_SALES_DRAFT_SCHEMA = {
	type: "object",
	additionalProperties: false,
	properties: {
		stop_entries: {
			type: "array",
			description: "What can't be sold and when, down to room-type / rate-plan.",
			items: {
				type: "object",
				properties: {
					product_kind: {
						type: "string",
						enum: ["hotel", "room_type", "course", "rate_plan", "board", "allocation", "other"],
					},
					product_name: { type: "string" },
					parent_property: { type: ["string", "null"] },
					ranges: {
						type: "array",
						items: {
							type: "object",
							properties: {
								start: { type: "string" },
								end: { type: ["string", "null"] },
							},
						},
					},
					scope: {
						type: "string",
						enum: ["hard_stop", "on_request", "free_sell_resumes", "other"],
					},
					reason: { type: ["string", "null"] },
					notes: { type: ["string", "null"] },
				},
			},
		},
	},
}

const PROMOTIONS_DRAFT_SCHEMA = {
	type: "object",
	additionalProperties: false,
	properties: {
		offers: {
			type: "array",
			description: "Typed offers with booking + stay windows.",
			items: {
				type: "object",
				properties: {
					name: { type: "string" },
					offer_type: {
						type: "string",
						enum: [
							"percentage_off",
							"fixed_amount_off",
							"free_nights",
							"free_rounds",
							"value_add",
							"early_booking",
							"other",
						],
					},
					value_pct: { type: ["number", "null"] },
					value_native: { type: ["number", "null"] },
					currency: { type: ["string", "null"] },
					free_quantity: { type: ["number", "null"] },
					booking_window: {
						type: "object",
						properties: { start: { type: "string" }, end: { type: ["string", "null"] } },
					},
					stay_window: {
						type: "object",
						properties: { start: { type: "string" }, end: { type: ["string", "null"] } },
					},
					applies_to: { type: "array", items: { type: "string" } },
					conditions: { type: ["object", "null"] },
					notes: { type: ["string", "null"] },
				},
			},
		},
	},
}

// Top-down layout: the pipeline flows down the page, the specialist readers
// fan out across a single row, then converge on the index step.
const READER_Y = 430

export const SUPPLIER_DOCUMENT_FLOW = {
	id: SUPPLIER_DOCUMENT_FLOW_ID,
	label: "Supplier document pipeline",
	description:
		"How an inbound supplier document is classified and fanned out to the specialist readers, then indexed. Mirrors the functions-suppliers orchestrator.",
	nodes: [
		{
			id: "trigger",
			type: "trigger",
			position: { x: 520, y: 0 },
			data: {
				label: "Document arrives",
				description:
					"A supplier document lands in supplier_contract_documents (manual upload or email forward) and Process is run.",
			},
		},
		{
			id: "document-classifier",
			type: "agent",
			position: { x: 500, y: 130 },
			data: {
				label: "Supplier document classifier",
				binding: "tool",
				toolId: "document-classifier",
				status: "live",
			},
		},
		{
			id: "router",
			type: "router",
			position: { x: 520, y: 270 },
			data: {
				label: "Route on classification",
				description:
					"Fans out on the classifier verdict, and for rate cards on content_types[]. contract / information are classify-only.",
			},
		},
		{
			id: "accommodation-reader",
			type: "agent",
			position: { x: 40, y: READER_Y },
			data: {
				label: "Accommodation reader",
				binding: "tool",
				toolId: "accommodation-reader",
				status: "live",
			},
		},
		{
			id: "green-fees-reader",
			type: "agent",
			position: { x: 300, y: READER_Y },
			data: {
				label: "Green fees reader",
				binding: "tool",
				toolId: "green-fees-reader",
				status: "live",
			},
		},
		{
			id: "packages-reader",
			type: "agent",
			position: { x: 560, y: READER_Y },
			data: {
				label: "Packages reader",
				binding: "tool",
				toolId: "packages-reader",
				status: "live",
			},
		},
		{
			id: "stop-sales-reader",
			type: "agent",
			position: { x: 820, y: READER_Y },
			data: {
				label: "Stop sales reader",
				binding: "placeholder",
				status: "planned",
				draft: {
					model: "gpt-4.1-mini",
					temperature: 0,
					system_instructions:
						"PLANNED - not yet wired. Captures what can't be sold and when, down to room-type / rate-plan, with stop ranges + scope (hard / on-request). See docs/supplier-document-extraction.md section 6.2.",
					user_instructions: "",
					json_schema: STOP_SALES_DRAFT_SCHEMA,
					json_schema_name: "supplier_stop_sales",
					json_schema_strict: false,
				},
			},
		},
		{
			id: "promotions-reader",
			type: "agent",
			position: { x: 1080, y: READER_Y },
			data: {
				label: "Promotions reader",
				binding: "placeholder",
				status: "planned",
				draft: {
					model: "gpt-4.1-mini",
					temperature: 0,
					system_instructions:
						"PLANNED - not yet wired. Captures typed offers (offer_type enum + value + booking/stay windows + conditions). See docs/supplier-document-extraction.md section 6.3.",
					user_instructions: "",
					json_schema: PROMOTIONS_DRAFT_SCHEMA,
					json_schema_name: "supplier_promotions",
					json_schema_strict: false,
				},
			},
		},
		{
			id: "index",
			type: "step",
			position: { x: 560, y: 600 },
			data: {
				label: "Index",
				description:
					"Deterministic step. Denormalises the common queryable layer (product_types[], months_available[], prices, room types, courses) onto the document for every classification.",
			},
		},
	],
	edges: [
		{
			id: "trigger->classifier",
			source: "trigger",
			target: "document-classifier",
			data: { condition: { kind: "always" }, note: "" },
		},
		{
			id: "classifier->router",
			source: "document-classifier",
			target: "router",
			data: { condition: { kind: "always" }, note: "" },
		},
		{
			id: "router->accommodation",
			source: "router",
			target: "accommodation-reader",
			data: {
				condition: { kind: "content_type", value: "accommodation" },
				note: "rate_card with accommodation content",
			},
		},
		{
			id: "router->green-fees",
			source: "router",
			target: "green-fees-reader",
			data: {
				condition: { kind: "content_type", value: "green_fee" },
				note: "rate_card with green-fee content",
			},
		},
		{
			id: "router->packages",
			source: "router",
			target: "packages-reader",
			data: {
				condition: { kind: "content_type", value: "package" },
				note: "rate_card with package content",
			},
		},
		{
			id: "router->stop-sales",
			source: "router",
			target: "stop-sales-reader",
			data: {
				condition: { kind: "classification", value: "supplier.stop_sales" },
				note: "",
			},
		},
		{
			id: "router->promotions",
			source: "router",
			target: "promotions-reader",
			data: {
				condition: { kind: "classification", value: "supplier.discount_sales" },
				note: "",
			},
		},
		{
			id: "accommodation->index",
			source: "accommodation-reader",
			target: "index",
			data: { condition: { kind: "always" }, note: "" },
		},
		{
			id: "green-fees->index",
			source: "green-fees-reader",
			target: "index",
			data: { condition: { kind: "always" }, note: "" },
		},
		{
			id: "packages->index",
			source: "packages-reader",
			target: "index",
			data: { condition: { kind: "always" }, note: "" },
		},
		{
			id: "stop-sales->index",
			source: "stop-sales-reader",
			target: "index",
			data: { condition: { kind: "always" }, note: "" },
		},
		{
			id: "promotions->index",
			source: "promotions-reader",
			target: "index",
			data: { condition: { kind: "always" }, note: "" },
		},
		{
			id: "router->index",
			source: "router",
			target: "index",
			data: {
				condition: { kind: "classification", value: "supplier.contract" },
				note: "contract / information are classify-only, still indexed",
			},
		},
	],
}

export const AI_FLOWS = [SUPPLIER_DOCUMENT_FLOW]

export const getFlowSeedById = (id) =>
	AI_FLOWS.find((flow) => flow.id === id) || null
